import { PREF, type PrefsHandle } from './webledgerPrefs.js';

/**
 * The register's columns, and the two gestures that rearrange them — legacy's
 * `LEDGER_COLS`, `wireHead` and `ledSaveCols`.
 *
 * A register is the page people sit in all day, and what they want from it
 * differs by book: someone reconciling wants Balance wide and Memo narrow,
 * someone chasing a reference wants the opposite. Legacy lets the header be
 * dragged and resized and remembers the result. This is that, with two
 * changes.
 *
 * **The layout is a preference, not a device setting.** Legacy keeps it in
 * `localStorage`, so it is per browser profile and silently different on a
 * laptop. Everything else in this rebuild moved to the server-synced prefs
 * for exactly that reason (see webledgerPrefs.ts), and a column layout is a
 * fact about a person's way of working, not about a machine.
 *
 * **Widths are absolute pixels, and the table scrolls.** They were relative
 * shares first, which was wrong twice over and reported as such: on a narrow
 * window every column is a share of very little, so there is barely anything
 * to resize; and a share cannot make a column narrower than its own content
 * will allow, so the Balance column stayed stubbornly wide even though its
 * currency chips would happily have wrapped. `table-layout: fixed` plus
 * pixel widths fixes both — a column becomes exactly as wide as it is told,
 * content wraps inside it, and when the columns add up to more than the
 * window the register scrolls sideways the way a register should.
 *
 * Kept in its own module because the register file is already long, and
 * because the column *set* is the thing a future column (files, once
 * attachments exist) is added to — one list, with its cell renderer beside
 * its header, rather than four places to keep in step.
 */

export interface RegisterColumn<Row, Leg> {
  key: string;
  /** A function, because two of the labels depend on the account's type. */
  label: () => string;
  /** Extra classes for both the header and the body cells. */
  cls?: string;
  /** Starting width in CSS pixels. */
  width: number;
  cell: (row: Row) => string;
  /**
   * The cell this column contributes to a split detail row, if any.
   *
   * Per column rather than a run of `colspan`s, because a `colspan` encodes
   * the column order and would silently point at the wrong thing the moment
   * a column moved.
   */
  splitCell?: (leg: Leg) => string;
}

interface StoredLayout {
  order?: string[];
  widths?: Record<string, number>;
}

/** Narrow enough to be genuinely out of the way, wide enough to still be grabbable. */
const MIN_WIDTH = 44;

export interface ColumnLayout<Row, Leg> {
  /** The columns in their current order, with their current widths. */
  columns: Array<RegisterColumn<Row, Leg>>;
  /** `<thead>` markup, including the drag handles and resize grips. */
  headHtml(): string;
  /**
   * The `<table>`'s own width — the sum of its columns.
   *
   * Stated explicitly because `table-layout: fixed` on a table told to be
   * `width: 100%` distributes any difference across the columns, which would
   * quietly override the widths the reader chose.
   */
  tableStyle(): string;
  /** One `<td>` per column, in order. */
  rowCellsHtml(row: Row): string;
  /** One `<td>` per column for a split line; columns with no `splitCell` get an empty cell. */
  splitCellsHtml(leg: Leg): string;
  /**
   * A row that spans the table with a label and one column's content — the
   * opening balance. `content` is keyed by column, and anything not named
   * gets an empty cell; the label goes in whichever column is leftmost, so
   * it is still the first thing read however the columns were arranged.
   */
  spanRowCellsHtml(label: string, content: Record<string, string>): string;
  /**
   * Call after replacing the table's markup.
   *
   * `onChange(rerender)` fires for both gestures: `true` after a reorder,
   * which changes the cell order and so needs the body rebuilt, and `false`
   * after a resize, which only changed widths already applied in place. Both
   * store a layout, which is why resize notifies at all — the caller has a
   * "Reset columns" control to reveal.
   */
  wire(container: HTMLElement, onChange: (rerender: boolean) => void): void;
  /** Is a layout of the reader's own stored, rather than the defaults? */
  isCustomised(): boolean;
  /**
   * Back to the shipped order and widths.
   *
   * The way out. A layout you have made unusable is not something to fix by
   * dragging it back — you cannot aim at a column you can no longer see — and
   * a reload does not help, because the layout is exactly the kind of thing
   * that is remembered.
   */
  reset(): void;
}

export function createColumnLayout<Row, Leg>(
  base: ReadonlyArray<RegisterColumn<Row, Leg>>, prefs: PrefsHandle,
): ColumnLayout<Row, Leg> {
  const columns = base.map((c) => ({ ...c }));
  restore();

  function restore(): void {
    const raw = prefs.get(PREF.registerColumns);
    if (!raw) return;
    let stored: StoredLayout;
    try {
      stored = JSON.parse(raw) as StoredLayout;
    } catch {
      return;   // a malformed layout costs the default, never an error
    }
    if (Array.isArray(stored.order)) {
      const order = stored.order;
      // A column added since the layout was saved sorts to the end rather
      // than disappearing — the stored order names what it knew about, and
      // a new column has to appear somewhere.
      const rank = (key: string): number => {
        const at = order.indexOf(key);
        return at < 0 ? order.length + base.findIndex((c) => c.key === key) : at;
      };
      columns.sort((a, b) => rank(a.key) - rank(b.key));
    }
    for (const column of columns) {
      const width = stored.widths?.[column.key];
      if (typeof width === 'number' && width >= MIN_WIDTH) column.width = width;
    }
  }

  function save(): void {
    const widths: Record<string, number> = {};
    for (const column of columns) widths[column.key] = Math.round(column.width);
    prefs.set(PREF.registerColumns, JSON.stringify({ order: columns.map((c) => c.key), widths }));
  }

  function total(): number {
    return columns.reduce((sum, c) => sum + c.width, 0);
  }

  return {
    columns,

    isCustomised(): boolean {
      return Boolean(prefs.get(PREF.registerColumns));
    },

    reset(): void {
      columns.splice(0, columns.length, ...base.map((c) => ({ ...c })));
      prefs.set(PREF.registerColumns, '');
    },

    tableStyle(): string {
      return `table-layout:fixed;width:${Math.round(total())}px`;
    },

    headHtml(): string {
      return `<thead><tr>${columns.map((c) => (
        `<th class="wl-col${c.cls ? ` ${c.cls}` : ''}" style="width:${Math.round(c.width)}px" draggable="true" data-col="${c.key}">`
        + `<span class="wl-col__label">${c.label()}</span>`
        // Not draggable itself, or the browser starts a column drag the
        // moment the grip is pressed and the resize never begins.
        + `<span class="wl-col__grip" data-col-grip="${c.key}" role="separator"`
        + ` aria-label="Resize the ${c.label() || 'column'} column" title="Drag to resize"></span>`
        + '</th>'
      )).join('')}</tr></thead>`;
    },

    rowCellsHtml(row: Row): string {
      return columns.map((c) => `<td class="${c.cls ?? ''}">${c.cell(row)}</td>`).join('');
    },

    splitCellsHtml(leg: Leg): string {
      return columns.map((c) => `<td class="${c.cls ?? ''}">${c.splitCell ? c.splitCell(leg) : ''}</td>`).join('');
    },

    spanRowCellsHtml(label: string, content: Record<string, string>): string {
      return columns.map((c, index) => {
        const own = content[c.key];
        if (own !== undefined) return `<td class="${c.cls ?? ''}">${own}</td>`;
        return index === 0 ? `<td class="text-secondary">${label}</td>` : '<td></td>';
      }).join('');
    },

    wire(container: HTMLElement, onChange: (rerender: boolean) => void): void {
      const head = container.querySelector<HTMLElement>('thead');
      if (!head) return;

      /**
       * Resize this column, and only this column.
       *
       * The first version took the width from the next column so the total
       * stayed at 100%. That is what made a narrow window unworkable: with
       * nothing to give, nothing could grow. Here the table simply gets wider
       * or narrower and its scroll container deals with it — which is also
       * why a column can now be dragged genuinely narrow without having to
       * find somewhere to put the space.
       */
      /**
       * True while a resize is under way, so the header's own drag-to-reorder
       * cannot start on top of it.
       *
       * `preventDefault()` on `pointerdown` is not enough on its own: whether
       * it suppresses the native drag depends on the browser, and when it
       * does not the column jumps to another position instead of resizing —
       * reported as "resize moves the column instead of resizing it". Turning
       * `draggable` off for the duration is legacy's own fix and is the only
       * one that holds everywhere; the `dragstart` guard below is the belt to
       * its braces.
       */
      let resizing = false;

      for (const grip of head.querySelectorAll<HTMLElement>('[data-col-grip]')) {
        grip.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          event.stopPropagation();
          resizing = true;
          const th = grip.closest<HTMLElement>('th');
          if (th) th.draggable = false;
          const column = columns.find((c) => c.key === grip.dataset['colGrip']);
          if (!column) {
            resizing = false;
            if (th) th.draggable = true;
            return;
          }

          const table = container.querySelector<HTMLElement>('table');
          const startX = event.clientX;
          const startWidth = column.width;
          grip.setPointerCapture(event.pointerId);

          const move = (moveEvent: PointerEvent): void => {
            column.width = Math.max(MIN_WIDTH, startWidth + (moveEvent.clientX - startX));
            if (th) th.style.width = `${Math.round(column.width)}px`;
            // The table is exactly as wide as its columns, so it has to move
            // with them or `table-layout: fixed` starts distributing the
            // difference and the drag stops tracking the pointer.
            if (table) table.style.width = `${Math.round(total())}px`;
          };
          const up = (): void => {
            grip.removeEventListener('pointermove', move);
            grip.removeEventListener('pointerup', up);
            grip.removeEventListener('pointercancel', up);
            if (th) th.draggable = true;
            // Released on the next frame, not here: the click that ends the
            // drag can still produce a `dragstart` in the same turn, and
            // clearing the flag immediately would let that through.
            requestAnimationFrame(() => { resizing = false; });
            save();
            onChange(false);
          };
          grip.addEventListener('pointermove', move);
          grip.addEventListener('pointerup', up);
          grip.addEventListener('pointercancel', up);
        });
      }

      // Reorder. Native drag and drop, as legacy has it — the header is the
      // one place in this app where a drag means "move this thing", and the
      // row drag below it already uses the same mechanism.
      let dragging: string | undefined;
      for (const th of head.querySelectorAll<HTMLElement>('th[data-col]')) {
        th.addEventListener('dragstart', (event) => {
          if (resizing) { event.preventDefault(); return; }
          dragging = th.dataset['col'];
          th.classList.add('wl-col--dragging');
          event.dataTransfer?.setData('text/plain', dragging ?? '');
          if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
        });
        th.addEventListener('dragend', () => {
          dragging = undefined;
          for (const other of head.querySelectorAll('th')) {
            other.classList.remove('wl-col--dragging', 'wl-col--over');
          }
        });
        th.addEventListener('dragover', (event) => {
          if (!dragging || th.dataset['col'] === dragging) return;
          event.preventDefault();
          for (const other of head.querySelectorAll('th')) other.classList.remove('wl-col--over');
          th.classList.add('wl-col--over');
        });
        th.addEventListener('dragleave', () => th.classList.remove('wl-col--over'));
        th.addEventListener('drop', (event) => {
          if (!dragging || th.dataset['col'] === dragging) return;
          event.preventDefault();
          const from = columns.findIndex((c) => c.key === dragging);
          const to = columns.findIndex((c) => c.key === th.dataset['col']);
          if (from < 0 || to < 0) return;
          const [moved] = columns.splice(from, 1);
          columns.splice(to, 0, moved!);
          dragging = undefined;
          save();
          // The body's cells are ordered by this array, so the whole table is
          // rebuilt rather than the header alone.
          onChange(true);
        });
      }
    },
  };
}
